import {
  AdvancedDataGrid,
  Avatar,
  createDataCardInitialColumnDefinition,
  DataGridRef,
  DateDisplay,
  formatDate,
  NestedDataCardProps,
  SelectedFilters,
} from "@inductiveautomation/ws-components";
import { capitalize } from "lodash";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { LicenseSession } from "../../Licenses.types";
import { useDemo } from "../../mocks/useDemo";
import { getLicenseStyles } from "../_licenseStyle";
import { matchesDateRangeFilter, matchesStringArrayFilter } from "../utilities";
import ActivationsDrawer from "./Activations.Drawer";

const Activations = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { classes } = getLicenseStyles();

  const { mockDataForLicense } = useDemo();
  const license = mockDataForLicense(id ?? "maker-license-1");

  const dataGridRef = useRef<DataGridRef>(null);
  const [activeSession, setActiveSession] = useState<LicenseSession>();
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>();

  const responseStatuses = license.sessions.reduce<string[]>(
    (statuses, session) => {
      const lowercaseStatus = session.status.toLowerCase();

      return statuses.includes(lowercaseStatus)
        ? statuses
        : [...statuses, lowercaseStatus];
    },
    []
  );

  const filteredData = license.sessions
    .filter((session) => {
      const dateMatches = matchesDateRangeFilter(
        selectedFilters?.["filter-date"],
        session.validated
      );
      const responseMatches = matchesStringArrayFilter(
        selectedFilters?.["filter-response"],
        `response-${session.status.toLowerCase()}`
      );

      return dateMatches && responseMatches;
    })
    .map(activationToDataCardMapper);

  return (
    <div className={classes.dataGridContainer}>
      <AdvancedDataGrid
        ref={dataGridRef}
        multiSelect={false}
        chromeless
        testId="license-activations"
        headings={[
          {
            id: "license-activations-heading-details",
            label: t(
              "maker_license.tabs.activations.data_grid.headings.details"
            ),
          },
          {
            id: "license-activations-heading-date",
            label: t("maker_license.tabs.activations.data_grid.headings.date"),
          },
          {
            id: "license-activations-heading-response",
            label: t(
              "maker_license.tabs.activations.data_grid.headings.response"
            ),
          },
        ]}
        data={filteredData}
        filterChips={[
          {
            id: "filter-date",
            label: t("maker_license.tabs.activations.data_grid.filters.date"),
            variant: "date_range_picker",
          },
          {
            id: "filter-response",
            label: t(
              "maker_license.tabs.activations.data_grid.filters.response"
            ),
            variant: "list_advanced",
            listItems: [
              responseStatuses.map((status) => ({
                id: `response-${status}`,
                label: capitalize(status),
                variant: "checkbox",
              })),
            ],
          },
        ]}
        selectedFilters={selectedFilters}
        onFiltersUpdated={setSelectedFilters}
        onSelection={(rowId) => {
          const session = license.sessions.find((s) => s.id === rowId);
          setActiveSession(session);
        }}
        generateCsvData={() => ({
          headings: [
            t("maker_license.tabs.activations.data_grid.fields.name"),
            t("maker_license.tabs.activations.data_grid.fields.ip_address"),
            t("maker_license.tabs.activations.data_grid.headings.date"),
            t("maker_license.tabs.activations.data_grid.headings.response"),
          ],
          rows: license.sessions.map((session) => [
            session.name,
            session.ipAddress,
            formatDate(session.validated, "date_and_time"),
            "[Activation-Response]",
          ]),
        })}
        labels={{
          singular_item_name: t(
            "maker_license.tabs.activations.data_grid.singular_item_name"
          ),
          plural_item_name: t(
            "maker_license.tabs.activations.data_grid.plural_item_name"
          ),
        }}
      />

      <ActivationsDrawer
        session={activeSession}
        onClose={() => {
          setActiveSession(undefined);
          dataGridRef.current?.clearCheckedAndSelected();
        }}
      />
    </div>
  );
};

export default Activations;

const activationToDataCardMapper = (
  session: LicenseSession
): NestedDataCardProps => {
  return {
    id: session.id,
    variant: "default",
    contentColumns: [
      createDataCardInitialColumnDefinition({
        parentTestId: session.id,
        avatar: <Avatar icon="session" iconAvatarColor="primary" />,
        heading: session.name,
        subtext: session.ipAddress,
      }),
      { content: <DateDisplay date={session.validated} /> },
      {
        content: "[Activation-Response]",
      },
    ],
    actionLists: [],
    childItems: [],
  };
};
